import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { ArrowRight, CheckCircle2, XCircle, Loader2, ClipboardList } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return path;
};

// Fields we display side-by-side, in this order, with pretty labels.
const DISPLAY_FIELDS = [
  ['title', 'Title'],
  ['location', 'Location'],
  ['country', 'Country'],
  ['category', 'Category'],
  ['type', 'Type'],
  ['listing_type', 'Listing Type'],
  ['price', 'Price / night (INR)'],
  ['guests', 'Guests'],
  ['bedrooms', 'Bedrooms'],
  ['beds', 'Beds'],
  ['bathrooms', 'Bathrooms'],
  ['description', 'Description'],
  ['amenities', 'Amenities'],
  ['images', 'Images'],
  ['rooms', 'Rooms'],
];

const formatValue = (key, value) => {
  if (value == null || value === '') return <span className="text-gray-400 italic">—</span>;
  if (key === 'amenities' && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.length === 0 && <span className="text-gray-400 italic">—</span>}
        {value.map((a, i) => (
          <span key={i} className="inline-block px-2 py-0.5 rounded bg-gray-100 text-xs">{a}</span>
        ))}
      </div>
    );
  }
  if (key === 'images' && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-2">
        {value.slice(0, 6).map((img, i) => (
          <img
            key={i}
            src={resolveImageUrl(img)}
            alt=""
            className="w-16 h-16 object-cover rounded border"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ))}
        {value.length > 6 && <span className="text-xs text-gray-500 self-center">+{value.length - 6} more</span>}
      </div>
    );
  }
  if (key === 'rooms' && Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.length === 0 && <span className="text-gray-400 italic">—</span>}
        {value.map((r, i) => (
          <div key={i} className="text-xs bg-gray-50 border rounded p-2">
            <div className="font-medium">{r.category || `Room ${i + 1}`}</div>
            <div className="text-gray-600">
              ₹{r.price?.toLocaleString?.() ?? r.price} · {r.beds || 1} bed · {r.max_guests || 2} guests · Qty {r.inventory || 1}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value.length > 300 ? value.slice(0, 300) + '…' : value;
  return String(value);
};

const isChanged = (a, b) => {
  if (a === b) return false;
  if (a == null && b == null) return false;
  try {
    return JSON.stringify(a) !== JSON.stringify(b);
  } catch (_e) {
    return a !== b;
  }
};

const DiffCard = ({ property, onApprove, onReject }) => {
  const changes = property.pending_edit?.changes || {};
  const changedKeys = Object.keys(changes);
  const submittedAt = property.pending_edit?.submitted_at;

  return (
    <Card data-testid={`property-edit-card-${property.id}`}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <img
              src={resolveImageUrl(property.images?.[0])}
              alt=""
              className="w-14 h-14 object-cover rounded flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{property.title}</h3>
              <p className="text-xs text-gray-500 truncate">{property.location}</p>
              <p className="text-xs text-gray-400 mt-1">
                Submitted: {submittedAt ? new Date(submittedAt).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
            {changedKeys.length} field{changedKeys.length !== 1 ? 's' : ''} changed
          </Badge>
        </div>

        {/* Diff table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[140px_1fr_20px_1fr] bg-gray-50 border-b text-xs font-semibold text-gray-600">
            <div className="p-2">Field</div>
            <div className="p-2">Current (Live)</div>
            <div />
            <div className="p-2">Proposed</div>
          </div>
          {DISPLAY_FIELDS.map(([key, label]) => {
            if (!(key in changes)) return null;
            const current = property[key];
            const proposed = changes[key];
            const changed = isChanged(current, proposed);
            if (!changed) return null;
            return (
              <div
                key={key}
                className="grid grid-cols-[140px_1fr_20px_1fr] border-b last:border-b-0 text-sm"
                data-testid={`diff-row-${key}`}
              >
                <div className="p-2 font-medium text-gray-700 bg-gray-50/50">{label}</div>
                <div className="p-2 text-gray-700 bg-red-50/40 break-words">
                  {formatValue(key, current)}
                </div>
                <div className="flex items-center justify-center text-gray-400">
                  <ArrowRight className="h-4 w-4" />
                </div>
                <div className="p-2 text-gray-900 bg-green-50/60 break-words">
                  {formatValue(key, proposed)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button
            variant="outline"
            onClick={() => onReject(property)}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-edit-${property.id}`}
          >
            <XCircle className="h-4 w-4 mr-1.5" /> Reject
          </Button>
          <Button
            onClick={() => onApprove(property)}
            className="bg-[#7CB342] hover:bg-[#6da036]"
            data-testid={`approve-edit-${property.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve & Publish
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminPropertyEditsTab = ({ onCountChange }) => {
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmApprove, setConfirmApprove] = useState(null);

  const token = localStorage.getItem('adminToken');

  const fetchEdits = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/property-edits`, {
        params: { token },
      });
      const list = res.data?.data || [];
      setEdits(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load property edits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEdits();
  }, []);

  const approve = async (property) => {
    try {
      setBusyId(property.id);
      await axios.post(
        `${BACKEND_URL}/api/admin/property-edits/${property.id}/approve`,
        { token }
      );
      toast.success('Edit approved and published to the live listing');
      setConfirmApprove(null);
      await fetchEdits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve edit');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      setBusyId(rejectTarget.id);
      await axios.post(
        `${BACKEND_URL}/api/admin/property-edits/${rejectTarget.id}/reject`,
        { token, reason: rejectReason.trim() }
      );
      toast.success('Edit rejected — host has been notified');
      setRejectTarget(null);
      setRejectReason('');
      await fetchEdits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject edit');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-[#7CB342] mx-auto" />
        <p className="mt-3 text-gray-600 text-sm">Loading pending edits…</p>
      </div>
    );
  }

  if (edits.length === 0) {
    return (
      <div className="text-center py-20 text-gray-500" data-testid="empty-property-edits">
        <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <p>No property edits awaiting review right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-property-edits-list">
      {edits.map((p) => (
        <DiffCard
          key={p.id}
          property={p}
          onApprove={(prop) => setConfirmApprove(prop)}
          onReject={(prop) => { setRejectTarget(prop); setRejectReason(''); }}
        />
      ))}

      {/* Approve confirm dialog */}
      <Dialog open={!!confirmApprove} onOpenChange={(open) => !open && setConfirmApprove(null)}>
        <DialogContent data-testid="approve-edit-dialog">
          <DialogHeader>
            <DialogTitle>Approve edit?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            The proposed changes for <strong>{confirmApprove?.title}</strong> will be published immediately
            to the live listing. Guests will see the new content on next page load.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmApprove(null)}>Cancel</Button>
            <Button
              onClick={() => approve(confirmApprove)}
              disabled={busyId === confirmApprove?.id}
              className="bg-[#7CB342] hover:bg-[#6da036]"
              data-testid="confirm-approve-edit"
            >
              {busyId === confirmApprove?.id ? 'Publishing…' : 'Yes, publish now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent data-testid="reject-edit-dialog">
          <DialogHeader>
            <DialogTitle>Reject property edit</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Tell the host why these edits weren&apos;t approved. This message will be emailed to them
              and shown on their dashboard.
            </p>
            <Label htmlFor="reject-reason" className="text-sm">Reason *</Label>
            <Textarea
              id="reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., New price doesn't match category norms, images unclear…"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              onClick={reject}
              disabled={busyId === rejectTarget?.id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-edit"
            >
              {busyId === rejectTarget?.id ? 'Rejecting…' : 'Reject edit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPropertyEditsTab;
